import { get } from 'lodash-es';

import {
  COMPANY_SELECTOR_SELECTED_COMPANY_STORAGE_KEY,
  PRIMARY_TOKEN,
} from '~constants/consts';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { ROUTES } from '~router/routePaths';
import { buildSignInPathWithRedirect } from '~utils/signInRedirect';

const IMPERSONATION_TOKEN = 'impersonationToken';
const IMPERSONATOR_NAME = 'impersonatorName';

// reading storage throws in a browser configured to block it (Safari private
// mode, embedded webviews), and this runs on the request interceptor's hot path
// where a throw would replace the caller's own error.
const readStorage = (storage: Storage, key: string) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

export const getPrimaryToken = () => readStorage(localStorage, PRIMARY_TOKEN);

export const getImpersonationToken = () =>
  readStorage(sessionStorage, IMPERSONATION_TOKEN);

export const isImpersonating = () => Boolean(getImpersonationToken());

// the impersonation token wins while one exists: every request in this tab acts
// as the impersonated user, and the admin's own PRIMARY_TOKEN is left untouched
// so stopping restores their session without a fresh sign-in.
export const getActiveToken = () => getImpersonationToken() ?? getPrimaryToken();

export const hasActiveSession = () => Boolean(getActiveToken());

// only for the header, which has no other record of who is impersonating once
// the page reloads into the impersonated user's session.
export const getImpersonatorName = () => readStorage(sessionStorage, IMPERSONATOR_NAME);

// the api stamps the kind of session it minted into the token's first audience
// (SessionJwt#strategy): `horizon-api-<rails-env>-impersonation` for an
// impersonation, the Stytch project id for an ordinary sign-in. the env segment
// is why this is matched rather than compared.
const IMPERSONATION_AUDIENCE_PATTERN = /^horizon-api-.+-impersonation$/;

const decodeJwtPayload = (token: string): unknown => {
  const payload = token.split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');

    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
  } catch {
    return null;
  }
};

// which session a token belongs to, read off the token itself. nothing here
// trusts it — the api verifies every token it is sent; this only decides which
// of the two buckets a refreshed token is written back to.
export const getSessionTokenScope = (token: string) => {
  const audience = get(decodeJwtPayload(token), 'aud');
  const sessionKind = Array.isArray(audience) ? audience[0] : audience;

  if (typeof sessionKind !== 'string') {
    return 'unknown' as const;
  }

  return IMPERSONATION_AUDIENCE_PATTERN.test(sessionKind)
    ? ('impersonation' as const)
    : ('primary' as const);
};

export const setPrimaryToken = (token: string) => {
  localStorage.setItem(PRIMARY_TOKEN, token);
};

const clearPrimaryToken = () => {
  localStorage.removeItem(PRIMARY_TOKEN);
};

export const setImpersonationToken = (token: string) => {
  sessionStorage.setItem(IMPERSONATION_TOKEN, token);
};

// posthog persists its distinct id across a reload, so without a reset the
// impersonated user's events would be attributed to the admin.
const resetSessionScopedState = () => {
  AnalyticsController.reset();
  // the company selector's choice belongs to the session being left behind.
  localStorage.removeItem(COMPANY_SELECTOR_SELECTED_COMPANY_STORAGE_KEY);
};

// start and stop both reload rather than swap state in place, so the app boots
// through the same hydration path as a plain refresh and no component has to
// track the transition.
//
// the impersonation token is written last: if the name write or the reset
// throws, no token has been published yet, so the caller's failure toast
// reflects reality instead of leaving an unreloaded page silently
// authenticating as the impersonated user.
export const startImpersonation = (token: string, impersonatorName: string) => {
  sessionStorage.setItem(IMPERSONATOR_NAME, impersonatorName);
  resetSessionScopedState();
  setImpersonationToken(token);

  window.location.replace('/');
};

export const clearImpersonation = () => {
  sessionStorage.removeItem(IMPERSONATION_TOKEN);
  sessionStorage.removeItem(IMPERSONATOR_NAME);
};

export const stopImpersonation = () => {
  clearImpersonation();
  resetSessionScopedState();

  window.location.replace('/');
};

// shared by every 401 handler: an impersonation token expires long before the
// admin's own session, so drop back to that session; otherwise the primary
// session itself is gone, so clear it and send the user to sign-in.
export const endUnauthenticatedSession = () => {
  if (isImpersonating()) {
    stopImpersonation();

    return;
  }

  clearPrimaryToken();

  const { hash, pathname, search } = window.location;
  if (pathname === `/${ROUTES.signIn}`) {
    return;
  }

  // an expired token is only discovered once the user hydrates, after the route
  // guard has already let this page through, so the OAuth request has to be carried
  // to sign-in here or the CLI's authorization is lost.
  const signInPath =
    pathname === `/${ROUTES.oauthAuthorize}`
      ? buildSignInPathWithRedirect(`/${ROUTES.signIn}`, `${pathname}${search}${hash}`)
      : `/${ROUTES.signIn}`;

  window.location.replace(signInPath);
};
